import { useState, useEffect } from 'react';
import { PDPHeaderCard, createPDPRedirectionUrl } from '@commerce-product-webcomponents/ui';
import { ProductContainer } from '@commerce-product-webcomponents/ui-composite';
import {
  productApi,
  productDataTransformer,
  IProduct,
  ProductData,
  productSeoTransformer,
  IProductSeo,
  VariantGroup,
  OptionMap,
  PDPBreadcrumbTransformer,
  BreadcrumbProps,
} from '@commerce-product-webcomponents/sdk';
import { Errors, ErrorCard, useSharecode } from '@commerce-webcomponents/ui';
import { WebEvents, WebEventsEnum } from '@commerce-webcomponents/sdk';
import {
  sendUtagView,
  transformerRegistry,
  useTealiumDataLayer,
  useNextgenRouter,
  useTranslation,
} from '@nextgen-web-framework/all';
import type { SubscribeEventPaylod } from '@commerce-subscription-webcomponents/ui-composite';
import { DataFlowEnum } from '@commerce-subscription-webcomponents/ui-composite';
import { Logger, ConfigService } from '@nextgen-web-framework/core';
import { Plans, subscriptionApi } from '@commerce-subscription-webcomponents/sdk';
import SEOComponent from '../../../components/SEOComponent/SEOComponent';
import Layout from '../../../components/Layout/Layout';
import { AppState, useAppSelector } from '../../../feature/store';
import { EventNameEnum } from '../../../components/Tealium/types/propTypes';
import withNextGenISR from '../../../utils/withNextGenISR';
import { PRODUCT, SUBSCRIPTION } from '../../../utils/service-keys';
import { CMSTransformerDataType, LocalStorageKeys, PageCategory, STATUS_CODE } from '../../../types/common';
import Styles from './product.module.scss';
import { getContentEssentials } from 'utils/getEssentials';
import { getHomeBreadcrumb, getInvalidateTime, getRequestOptions } from 'utils/common';
import logObject from 'utils/logObject';
import { getLocale } from 'i18n.config';
import withNextgenRoute from 'utils/withNextgenRoute';
import NotFound from 'components/NotFound/NotFound';
import withFeatureFlags from 'utils/withFeatureFlags';

const logger = Logger.getInstance(logObject('[pid].tsx', 'Product'));
export interface ProductDetailsPageProps {
  initialProduct: IProduct;
  initialProductData: ProductData;
  initialProductSeoData: IProductSeo;
  initialSubscriptionData: Plans;
  initialSubscriptionError: boolean;
  data: CMSTransformerDataType;
  errorCode?: number;
}
const ProductDetailPage = ({
  initialProduct,
  initialProductData,
  initialProductSeoData,
  initialSubscriptionData,
  initialSubscriptionError,
  data,
  errorCode,
}: ProductDetailsPageProps) => {
  const [productDetails, setProductDetails] = useState({ id: '', name: '' });
  const router = useNextgenRouter();
  let pid = router.query?.get('pid');
  // Temp change until product remove vgid from url
  pid = pid?.split('-')[0];
  const sop = router.query?.get('sop');
  const { sharecodeData } = useSharecode();
  const [sendViewEvent, setSendViewEvent] = useState(true);
  const [productErrorCode, setProductErrorCode] = useState(errorCode);
  const [productClientErrorCode, setProductClientErrorCode] = useState(0);
  const t = useTranslation();
  const homeBreadcrumbData = getHomeBreadcrumb(t);
  const [breadCrumbData, setBreadCrumbData] = useState<BreadcrumbProps>({ breadcrumbData: [] });
  const dataLayer = useTealiumDataLayer();
  const { selectedSubscriptionPlan } = useAppSelector((state: AppState) => state.commerceSubscription);
  const config = ConfigService.getConfigs();
  const productNewRoute = config.NEXT_PDP_ROUTE_SEO_FEATURE;
  const [productClientData, setProductClientData] = useState({
    product: initialProduct,
    productData: initialProductData,
    productSeoData: initialProductSeoData,
  });
  const [subscriptionClientData, setSubscriptionClientData] = useState({
    subscriptionData: initialSubscriptionData,
    subscriptionError: initialSubscriptionError,
  });
  const { product, productData, productSeoData } = productClientData;
  const { subscriptionData, subscriptionError } = subscriptionClientData;
  useEffect(() => {
    if (sendViewEvent && product) {
      const transformedData = transformerRegistry[EventNameEnum.product_view](product);
      sendUtagView(EventNameEnum.product_view, dataLayer, transformedData);
      setSendViewEvent(false);
    }
  }, []);

  const fetchBreadCrumbData = () => {
    PDPBreadcrumbTransformer(product, getLocale(router.params?.locale as string))
      .then((data) => {
        if (data.breadcrumbData.length > 0) {
          setBreadCrumbData({ breadcrumbData: [homeBreadcrumbData, ...data.breadcrumbData] });
        }
      })
      .catch((e) => {
        logger.log('There is some error while generating breadcrumb data for PDP.', e);
      });
  };

  useEffect(() => {
    if (product) {
      fetchBreadCrumbData();
    }
  }, [product]);

  useEffect(() => {
    if (productDetails.id && pid !== productDetails.id) {
      const url = productNewRoute
        ? createPDPRedirectionUrl(productDetails.id, productDetails.name)
        : `/product/${productDetails.id}`;
      router.push(sop ? `${url}?sop=${sop}` : url);
    }
  }, [productDetails.id]);

  useEffect(() => {
    if (subscriptionError) {
      WebEvents.publishEvent({
        event: WebEventsEnum.ErrorMessage,
        payload: {},
        sync: false,
      });
    }
  }, [subscriptionError]);
  const fetchProductDataClient = async () => {
    const { product, productData, productErrorCode, subscriptionData, hasSubscriptionError, productSeoData } =
      await getProductPageEssentials(router.params?.locale as string, pid as string);
    if (productErrorCode) {
      setProductClientErrorCode(productErrorCode);
      setProductErrorCode(productErrorCode);
    } else {
      setProductClientData({
        product: product as IProduct,
        productData: productData as ProductData,
        productSeoData: productSeoData as IProductSeo,
      });
      setSubscriptionClientData({
        subscriptionData: subscriptionData as Plans,
        subscriptionError: hasSubscriptionError,
      });
    }
  };
  useEffect(() => {
    setProductErrorCode(errorCode);
    setProductClientData({
      product: initialProduct as IProduct,
      productData: initialProductData as ProductData,
      productSeoData: initialProductSeoData as IProductSeo,
    });
    setSubscriptionClientData({
      subscriptionData: initialSubscriptionData as Plans,
      subscriptionError: initialSubscriptionError,
    });
  }, [initialProduct.productId]);
  useEffect(() => {
    if (errorCode === STATUS_CODE.FORBIDDEN && pid) {
      fetchProductDataClient();
    }
  }, [errorCode, pid]);
  const handleProductRefresh = (optionData?: OptionMap, itemNumber?: string) => {
    const itemNum = optionData?.product?.items?.[0]?.itemNumber ?? itemNumber ?? '';
    const itemName = optionData?.product?.copy?.title ?? '';
    setProductDetails({ id: itemNum, name: itemName });
  };

  function showSubscribeButton(showButton: boolean) {
    return showButton;
  }

  const handleSubscription = ({ subscriptionId }: SubscribeEventPaylod) => {
    router.push(`/subscription-checkout/${subscriptionId}`);
  };
  // TODO: Will be feched via promotion sdd later
  const potentialPromotions =
    productData?.copy?.promotions?.map((p) => ({
      promotion: p,
    })) || [];
  if ((productErrorCode && productErrorCode !== STATUS_CODE.FORBIDDEN) || productClientErrorCode) {
    return productErrorCode === STATUS_CODE.NOT_FOUND || productClientErrorCode === STATUS_CODE.NOT_FOUND ? (
      <NotFound data={data} />
    ) : (
      <ErrorCard type={Errors.General} className="error-card-wrapper" onActionClick={() => router.refresh()} />
    );
  }
  return (
    <Layout
      hasTopMobileHeader={true}
      header={data?.layout?.header}
      footer={data?.layout?.footer}
      crumbsData={breadCrumbData}
      headerProps={{
        headerElement: product ? (
          <div className={Styles['product-header-card']}>
            <PDPHeaderCard
              link={product.mainLink ?? ''}
              alt={product.alt}
              brandName={product.brandName ?? ''}
              productName={product.productName}
            />
          </div>
        ) : (
          <></>
        ),
        hasTabletScrolledHeaderBody: true,
      }}
    >
      <div className={Styles['product-details-page']}>
        <SEOComponent {...productSeoData} />
        {product && (
          <ProductContainer
            productData={{ ...product }}
            handleDataRefresh={handleProductRefresh}
            disabled={false}
            entryObj={{
              productId: product.productId,
              link: product.mainLink ?? '',
              alt: product.alt,
              brandName: product.brandName ?? '',
              productName: product.productName,
              productSize: product.productSize,
              productPrice: product.productPrice,
              pointValue: product.pointValue,
              businessVolume: product.businessVolume,
              variantGroupData: product.variantData as VariantGroup,
            }}
            subscriptionPlans={subscriptionData}
            subscriptionPlanData={selectedSubscriptionPlan}
            dataFlow={DataFlowEnum.PDP}
            handleSubscription={handleSubscription}
            showSubscribeButton={showSubscribeButton}
            handleProductDataOrError={(productResponse) => {
              if (productResponse instanceof Response) {
                setProductErrorCode(productResponse.status);
              }
            }}
            promotionData={{
              firedPromotions: [],
              potentialPromotions,
            }}
            sharebarLocalStorageKey={LocalStorageKeys.SHARECODE_ATTRIBUTES}
            isSharedDataLoaded={!!sharecodeData}
          />
        )}
      </div>
    </Layout>
  );
};

export const getStaticPaths = () => ({
  paths: [],
  fallback: 'blocking', //indicates the type of fallback
});

const getProductEssentials = async (locale: string, pid: string) => {
  let product: IProduct | undefined;
  let productData: ProductData | Response | undefined;
  let productSeoData: IProductSeo | undefined;
  let productErrorCode: number | undefined;
  const isSSR = typeof window === 'undefined';
  try {
    productData = await productApi.getProductByIdOrError(
      {
        productId: pid as string,
      },
      {
        ...(isSSR ? getRequestOptions(locale as string, PRODUCT) : {}),
      },
    );
    if (!(productData instanceof Response)) {
      product = productDataTransformer(productData);
      productSeoData = productSeoTransformer(productData);
    } else {
      productErrorCode = productData.status;
    }
  } catch (err) {
    productErrorCode = STATUS_CODE.INTERNAL_SERVER_ERROR;
    logger.error('Product Api Error', err);
  }
  return {
    productData,
    product,
    productErrorCode,
    productSeoData,
  };
};

const getSubscriptionByProduct = async (locale: string, pid: string) => {
  let subscription: Plans | undefined;
  let subscriptionError = false;
  const isSSR = typeof window === 'undefined';
  try {
    subscription = await subscriptionApi.getPlanDetailsByProductId(
      {
        productId: pid as string,
      },
      {
        ...(isSSR ? getRequestOptions(locale as string, SUBSCRIPTION) : {}),
      },
    );
  } catch (error) {
    subscriptionError = true;
    logger.error('Product Page Subscription Api Error', error);
  }
  return {
    subscription,
    subscriptionError,
  };
};

const getProductPageEssentials = async (locale: string, pid: string) => {
  let subscriptionData: Plans | undefined;
  let hasSubscriptionError = false;
  const { product, productData, productErrorCode, productSeoData } = await getProductEssentials(locale, pid);
  if (!productErrorCode) {
    const { subscription, subscriptionError } = await getSubscriptionByProduct(locale, pid);
    subscriptionData = subscription;
    hasSubscriptionError = subscriptionError;
  }
  return {
    product,
    productData,
    subscriptionData,
    productErrorCode,
    hasSubscriptionError,
    productSeoData,
  };
};
export const getStaticProps = withNextGenISR(async (context) => {
  const locale = context.params?.locale as string;
  let pid = context.params?.pid as string;
  // Temp change until product remove vgid from url
  pid = pid?.split('-')[0];
  const pageContent = await getContentEssentials({ locale, uId: 'product' });
  const { data } = pageContent;
  const { showError } = pageContent;
  const { product, productData, productErrorCode, subscriptionData, hasSubscriptionError, productSeoData } =
    await getProductPageEssentials(locale, pid);
  return {
    props: {
      initialProduct: !productErrorCode && JSON.parse(JSON.stringify(product)),
      initialProductData: !productErrorCode && JSON.parse(JSON.stringify(productData)),
      initialProductSeoData: productSeoData,
      initialSubscriptionData: subscriptionData || ({} as Plans),
      showError: showError,
      errorCode: productErrorCode,
      initialSubscriptionError: hasSubscriptionError,
      notFoundPage: product?.productFlags?.restrictFromPdpDisplay,
      data,
    },
    revalidate: getInvalidateTime(PageCategory.DEFERRED),
  };
});

export default withNextgenRoute<Readonly<ProductDetailsPageProps>>(withFeatureFlags(ProductDetailPage));
